import { DECIMAL_RE } from "@/lib/format";

const MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12 };

const iso = (y, m, d) => {
  const [Y, M, D] = [Number(y), Number(m), Number(d)];
  const dt = new Date(Date.UTC(Y, M - 1, D));
  if (dt.getUTCFullYear() !== Y || dt.getUTCMonth() !== M - 1 || dt.getUTCDate() !== D) return null;
  return `${Y}-${String(M).padStart(2, "0")}-${String(D).padStart(2, "0")}`;
};
const year = (y) => (y.length === 2 ? `20${y}` : y);

// Accepts 2025-07-01, 1/7/2025, 01-07-25, 1-Jul-25, 1 July 2025 (Australian day-first order).
export function parseDate(raw) {
  const s = String(raw || "").trim();
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return iso(m[1], m[2], m[3]);
  m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/);
  if (m) return iso(year(m[3]), m[2], m[1]);
  m = s.match(/^(\d{1,2})[\s-]+([A-Za-z]{3,9})[\s,-]+(\d{2}|\d{4})$/);
  if (m && MONTHS[m[2].toLowerCase().slice(0, m[2].toLowerCase() === "sept" ? 4 : 3)]) {
    return iso(year(m[3]), MONTHS[m[2].toLowerCase().slice(0, 3)], m[1]);
  }
  return null;
}

// "$1,234.50", "(12.00)", "-5" -> "1234.50", "-12.00", "-5"; "" -> null; junk -> undefined
export function parseAmount(raw) {
  let s = String(raw || "").replace(/A\$|AUD|\$|,|\s/gi, "");
  if (!s) return null;
  if (/^\(.*\)$/.test(s)) s = `-${s.slice(1, -1)}`;
  return DECIMAL_RE.test(s) ? s : undefined;
}

const splitCsv = (line) => {
  const out = [];
  let cur = "", quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (quoted && line[i + 1] === '"') { cur += '"'; i++; } else quoted = !quoted;
    } else if (ch === "," && !quoted) { out.push(cur); cur = ""; } else cur += ch;
  }
  out.push(cur);
  return out;
};

// Columns: Date, Description, Received, Spent, Category, Notes, then optional For, What, Project (tab from Excel, or comma CSV).
export function parsePasted(text, categories, fy) {
  const byName = new Map((categories || []).map((c) => [c.name.trim().toLowerCase(), c]));
  const lines = String(text || "").replace(/\r/g, "").split("\n").filter((l) => l.trim());
  const rows = [];
  lines.forEach((line, idx) => {
    const cells = (line.includes("\t") ? line.split("\t") : splitCsv(line)).map((c) => c.trim());
    const [d, description = "", rec = "", sp = "", catName = "", notes = "", for_whom = "", what = "", project = ""] = cells;
    const date = parseDate(d);
    if (!date && idx === 0 && /date/i.test(d)) return; // header row
    const received = parseAmount(rec);
    const spent = parseAmount(sp);
    const errors = [];
    const warnings = [];
    if (!date) errors.push(`Unreadable date "${d}"`);
    if (received === undefined) errors.push(`Received "${rec}" isn't a number`);
    if (spent === undefined) errors.push(`Spent "${sp}" isn't a number`);
    const r = received && Number(received) !== 0 ? received : null;
    const s = spent && Number(spent) !== 0 ? spent : null;
    if (r && s) errors.push("Has both Received and Spent");
    if (!r && !s && received !== undefined && spent !== undefined) errors.push("No amount");
    const cat = catName ? byName.get(catName.toLowerCase()) : null;
    if (catName && !cat) warnings.push(`Unknown category "${catName}" — left blank`);
    if (!catName) warnings.push("Blank category");
    if (date && fy && (date < fy.start_date || date > fy.end_date)) warnings.push(`Date is outside ${fy.label}`);
    rows.push({ key: idx, date, rawDate: d, description, received: r, spent: s, category: cat?.id || null,
      categoryName: cat?.name || catName, notes, for_whom, what, project, errors, warnings });
  });
  return rows;
}
